import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { useTheme } from '../../../contexts/ThemeContext';
import { useDepartments } from '../../../hooks/useDepartments';
import { sessionService } from '../../../services/sessionService';
import { userService } from '../../../services/userService';
import LoadingSpinner from '../../common/LoadingSpinner';
import { EmptyState } from '../../common/EmptyState';
import UniversalFilters from '../../common/UniversalFilters';
import DebouncedInput from '../../ui/DebouncedInput';
import { getFilterConfig } from '../../../constants/filterConfigs';
import { USER_ROLES } from '../../../constants/authConstants';
import { useFilters } from '../../../hooks/useFilters';
import { formatSessionAmount, SESSION_CLEARANCE_TYPE_OPTIONS } from '../../../constants/sessionConstants';
import { 
  validateAllocatedAmount,
  validateUONumber,
  validateClearanceDate,
  sanitizeSessionClearance
} from '../../../utils/sessionValidation';
import { financialYearUtils, debounce, dateUtils, arrayUtils } from '../../../utils/helpers';
import ExportExcelButton from '../../common/ExportExcelButton';
import { exportClearanceProjectWiseToExcel } from '../../../utils/excelExport';
import { LOCK_MESSAGES } from '../../../constants/projectLockConstants';

const ClearanceProjectWise = () => {
  const { user, getUserRole } = useAuth();
  const { isMobile, getResponsiveValue } = useTheme();
  const { departments, loading: loadingDepartments, getDepartmentLabel } = useDepartments();
  
  const isAdmin = getUserRole() === USER_ROLES.ADMIN;
  
  const [loading, setLoading] = useState(true);
  const [projects, setProjects] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState({});
  const [userCache, setUserCache] = useState({});
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [showUpdatedByColumn, setShowUpdatedByColumn] = useState(false);
  const [exportingExcel, setExportingExcel] = useState(false);
  
  const [selectedFinancialYear, setSelectedFinancialYear] = useState(
    financialYearUtils.getActualCurrentFinancialYear()
  );
  
  const [viewAllYears, setViewAllYears] = useState(false);
  
  const [success, setSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  const {
    filters,
    handleFilterChange,
    clearFilters,
    hasActiveFilters
  } = useFilters({
    search: '',
    department: '',
    financialYear: financialYearUtils.getActualCurrentFinancialYear()
  });

  const filterConfig = getFilterConfig('CLEARANCE');

  const titleSize = getResponsiveValue({
    mobile: 'text-xl',
    desktop: 'text-2xl'
  });

  const cardPadding = getResponsiveValue({
    mobile: 'p-4',
    desktop: 'p-6'
  });

  // USER MANAGEMENT
  const getUserDisplayName = async (userId) => {
    if (!userId) return 'System';
    if (userCache[userId]) return userCache[userId];
    
    try {
      const userData = await userService.getUserById(userId);
      const displayName = userData ? 
        userData.fullName || userData.email || 'Unknown User'
        : 'Unknown User';
      
      setUserCache(prev => ({ ...prev, [userId]: displayName }));
      return displayName;
    } catch (error) {
      console.error('Error fetching user data:', error);
      const fallbackName = 'Unknown User';
      setUserCache(prev => ({ ...prev, [userId]: fallbackName }));
      return fallbackName;
    }
  };

  const formatLastUpdated = (updatedBy, updatedAt) => {
    const userId = updatedBy || 'system';
    const userDisplayName = userCache[userId] || (loadingUsers ? 'Loading...' : 'Unknown User');
    const timeAgo = updatedAt ? dateUtils.formatDateTime(updatedAt) : 'Unknown';
    return { user: userDisplayName, time: timeAgo };
  };

  const loadUserData = async (projects) => {
    const userIds = [...new Set(projects.map(p => p.updatedBy || p.createdBy).filter(Boolean))];
    if (userIds.length === 0) return;
    
    setLoadingUsers(true);
    try {
      await Promise.all(userIds.map(userId => getUserDisplayName(userId)));
    } catch (error) {
      console.error('Error loading user data:', error);
    } finally {
      setLoadingUsers(false);
    }
  };

  // LOCK/UNLOCK - CLEARANCE SPECIFIC
  const handleLockProject = async (projectId) => {
    if (!projectId) return;

    const project = projects.find(p => p.id === projectId);
    if (!project) return;

    if (project.isClearanceLocked) {
      alert(LOCK_MESSAGES.CLEARANCE_ALREADY_LOCKED);
      return;
    }

    if (!confirm(LOCK_MESSAGES.CLEARANCE_LOCK_CONFIRM)) return;

    try {
      const result = await sessionService.lockSessionClearance(
        projectId,
        user.uid,
        user.displayName || user.email
      );

      if (result.success) {
        setProjects(prev => prev.map(p => 
          p.id === projectId 
            ? { ...p, isClearanceLocked: true, clearanceLockedAt: new Date(), clearanceLockedBy: user.uid, clearanceLockedByName: user.displayName || user.email }
            : p
        ));

        setSuccess(true);
        setSuccessMessage(LOCK_MESSAGES.CLEARANCE_LOCK_SUCCESS);
        setTimeout(() => {
          setSuccess(false);
          setSuccessMessage('');
        }, 3000);
      }
    } catch (error) {
      console.error('Error locking clearance:', error);
      alert('Failed to lock clearance: ' + error.message);
    }
  };

  const handleUnlockProject = async (projectId) => {
    if (!isAdmin) {
      alert(LOCK_MESSAGES.CLEARANCE_LOCK_NO_PERMISSION);
      return;
    }

    if (!projectId) return;

    const project = projects.find(p => p.id === projectId);
    if (!project) return;

    if (!project.isClearanceLocked) {
      alert(LOCK_MESSAGES.CLEARANCE_NOT_LOCKED);
      return;
    }

    if (!confirm(LOCK_MESSAGES.CLEARANCE_UNLOCK_CONFIRM)) return;

    try {
      const result = await sessionService.unlockSessionClearance(
        projectId,
        user.uid,
        user.displayName || user.email
      );

      if (result.success) {
        setProjects(prev => prev.map(p => 
          p.id === projectId 
            ? { ...p, isClearanceLocked: false, clearanceLockedAt: null, clearanceLockedBy: null, clearanceLockedByName: null }
            : p
        ));

        setSuccess(true);
        setSuccessMessage(LOCK_MESSAGES.CLEARANCE_UNLOCK_SUCCESS);
        setTimeout(() => {
          setSuccess(false);
          setSuccessMessage('');
        }, 3000);
      }
    } catch (error) {
      console.error('Error unlocking clearance:', error);
      alert('Failed to unlock clearance: ' + error.message);
    }
  };

  const canEditProject = (project) => {
    if (isAdmin) return true;
    return !project.isClearanceLocked;
  };

  // DATA LOADING
  const applyFiltersAndSort = () => {
    let filtered = [...projects];

    if (filters.search && filters.search.trim()) {
      const searchFields = ['projectName', 'department', 'source'];
      filtered = arrayUtils.filterBySearch(filtered, filters.search, searchFields);
    }

    if (filters.department) {
      filtered = filtered.filter(project => project.department === filters.department);
    }

    setFilteredProjects(filtered);
  };

  const loadData = async () => {
    try {
      setLoading(true);
      
      const fyToLoad = viewAllYears ? null : selectedFinancialYear;
      
      const projectsResult = await sessionService.getSessionProjectsForClearance(fyToLoad);
      if (projectsResult.success) {
        const projectsWithLock = projectsResult.data.map(project => ({
          ...project,
          isClearanceLocked: project.isClearanceLocked || false,
          clearanceLockedAt: project.clearanceLockedAt || null,
          clearanceLockedBy: project.clearanceLockedBy || null,
          clearanceLockedByName: project.clearanceLockedByName || null
        }));
        
        setProjects(projectsWithLock);
        await loadUserData(projectsWithLock);
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  // EFFECTS
  useEffect(() => {
    if (!viewAllYears) {
      handleFilterChange('financialYear', selectedFinancialYear);
    }
  }, [selectedFinancialYear, viewAllYears]);

  useEffect(() => {
    if (departments.length > 0) {
      loadData();
    }
  }, [selectedFinancialYear, viewAllYears, departments]);

  useEffect(() => {
    applyFiltersAndSort();
  }, [projects, filters.search, filters.department]);

  // HANDLERS
  const handleFinancialYearChange = (year) => {
    setSelectedFinancialYear(year);
    setViewAllYears(false);
  };

  const handleViewAllYears = () => {
    setViewAllYears(!viewAllYears);
  };

  const handleDelete = async (projectId) => {
    const project = projects.find(p => p.id === projectId);
    if (!project) return;

    if (project.isClearanceLocked && !isAdmin) {
      alert(LOCK_MESSAGES.CLEARANCE_LOCKED_EDIT_WARNING);
      return;
    }

    if (!confirm('Are you sure you want to delete this project?')) return;
    
    try {
      await sessionService.deleteSessionProject(projectId);
      loadData();
      
      setSuccess(true);
      setSuccessMessage('Project deleted successfully!');
      setTimeout(() => {
        setSuccess(false);
        setSuccessMessage('');
      }, 3000);
    } catch (error) {
      console.error('Error deleting project:', error);
      alert('Failed to delete project: ' + error.message);
    }
  };

  const handleFieldChange = (projectId, field, value) => {
    const project = projects.find(p => p.id === projectId);
    if (!project) return;

    if (project.isClearanceLocked && !isAdmin) {
      alert(LOCK_MESSAGES.CLEARANCE_LOCKED_EDIT_WARNING);
      return;
    }

    setProjects(prev => 
      prev.map(p => 
        p.id === projectId ? { ...p, [field]: value } : p
      )
    );

    setErrors(prev => ({ ...prev, [`${projectId}.${field}`]: '' }));

    let fieldError = null;
    switch (field) {
      case 'allocatedAmount':
        fieldError = validateAllocatedAmount(value);
        break;
      case 'uoNumber':
        fieldError = validateUONumber(value);
        break;
      case 'clearanceDate':
        fieldError = validateClearanceDate(value);
        break;
    }

    if (fieldError) {
      setErrors(prev => ({ ...prev, [`${projectId}.${field}`]: fieldError }));
      return;
    }

    if (value !== undefined && value !== null && value !== '') {
      const currentProject = projects.find(p => p.id === projectId);
      if (currentProject) {
        const currentData = {
          allocatedAmount: currentProject.allocatedAmount || '',
          uoNumber: currentProject.uoNumber || '',
          clearanceDate: currentProject.clearanceDate || null,
          [field]: value
        };
        debouncedAutoSave(projectId, currentData);
      }
    }
  };

  const handleDateChange = (projectId, date) => {
    handleFieldChange(projectId, 'clearanceDate', date);
  };

  const debouncedAutoSave = useCallback(
    debounce(async (projectId, clearanceData) => {
      const project = projects.find(p => p.id === projectId);
      if (!project) return;

      if (project.isClearanceLocked && !isAdmin) {
        console.log('Cannot save: clearance is locked');
        return;
      }

      try {
        setSaving(prev => ({ ...prev, [projectId]: true }));
        
        const sanitizedData = sanitizeSessionClearance(clearanceData);
        await sessionService.updateSessionProjectClearance(projectId, sanitizedData, user.uid);
        
        setProjects(prev => prev.map(p => 
          p.id === projectId 
            ? { ...p, ...clearanceData, updatedBy: user.uid, updatedAt: new Date() }
            : p
        ));
        
        setSaving(prev => ({ ...prev, [projectId]: false }));
        
        if (!userCache[user.uid]) {
          await getUserDisplayName(user.uid);
        }
      } catch (error) {
        console.error('Auto-save error:', error);
        setSaving(prev => ({ ...prev, [projectId]: false }));
      }
    }, 1500),
    [user.uid, userCache, projects, isAdmin]
  );

  const handleExportExcel = () => {
    try {
      setExportingExcel(true);
      const exportData = { projects: filteredProjects };
      exportClearanceProjectWiseToExcel(exportData, filters, getDepartmentLabel);
    } catch (error) {
      console.error('Error exporting Excel:', error);
      alert('Failed to export Excel. Please try again.');
    } finally {
      setExportingExcel(false);
    }
  };

  // UTILITY
  const formatDateForInput = (date) => {
    if (!date) return '';
    const d = new Date(date);
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    return `${year}-${month}-${day}`;
  };

  const getClearanceTypeLabel = (clearanceType) => {
    if (!clearanceType) return '—';
    const option = SESSION_CLEARANCE_TYPE_OPTIONS.find(opt => opt.value === clearanceType);
    return option ? option.label : clearanceType;
  };

  const getGroupedProjects = () => {
    if (!filteredProjects.length || departments.length === 0) return {};
    
    const grouped = {};
    filteredProjects.forEach(project => {
      if (!grouped[project.department]) {
        grouped[project.department] = [];
      }
      grouped[project.department].push(project);
    });
    
    return grouped;
  };

  const groupedProjects = getGroupedProjects();
  
  const departmentsToShow = Object.keys(groupedProjects).sort((a, b) => {
    const deptA = departments.find(d => d.departmentCode === a);
    const deptB = departments.find(d => d.departmentCode === b);
    return (deptA?.priority || 999) - (deptB?.priority || 999);
  });

  const fyOptions = financialYearUtils.getExtendedFinancialYearOptions();

  if (loading || loadingDepartments) {
    return (
      <div className="min-h-screen bg-bg-secondary flex items-center justify-center">
        <LoadingSpinner size="xl" text="Loading clearance data..." />
      </div>
    );
  }

  return (
    <div>
      {success && (
        <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl p-3 shadow-sm mb-6">
          <div className="flex items-center">
            <svg className="w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <span className="ml-2 text-green-700 font-medium text-sm">{successMessage}</span>
          </div>
        </div>
      )}

      <style>{`
        .table-container {
          max-height: calc(100vh - 300px);
          overflow-y: auto;
          position: relative;
        }
        .sticky-header thead {
          position: sticky;
          top: 0;
          z-index: 20;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        }
        .sticky-header thead th {
          background: linear-gradient(to right, #f9fafb, #f3f4f6);
        }
        .no-arrows::-webkit-outer-spin-button,
        .no-arrows::-webkit-inner-spin-button {
          -webkit-appearance: none;
          margin: 0;
        }
        .no-arrows[type=number] {
          -moz-appearance: textfield;
        }
        .enhanced-input:focus {
          transform: translateY(-1px);
          box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
        }
        .enhanced-input {
          transition: all 0.2s ease-in-out;
        }
        .readonly-field {
          background-color: #f8fafc;
          border: 1px solid #e2e8f0;
          color: #4a5568;
          font-weight: 500;
        }
        .table-row:hover .enhanced-input {
          border-color: #3b82f6;
        }
      `}</style>

      <div className="flex items-center justify-between mb-4">
        <div>
          {selectedFinancialYear && !viewAllYears && (
            <p className="text-3xl font-semibold text-blue-600 mt-1">
              {financialYearUtils.getFinancialYearDisplay(selectedFinancialYear)}
            </p>
          )}
          <h1 className={`${titleSize} font-semibold text-text-primary mb-1`}>Project Wise</h1>
          <p className="text-text-secondary text-sm">
            Project-wise allocation and clearance tracking for session projects
          </p>
        </div>

        <div className="flex items-center space-x-4">
          <ExportExcelButton 
            onClick={handleExportExcel}
            loading={exportingExcel}
            disabled={filteredProjects.length === 0}
          />
          
          <button
            onClick={() => setShowUpdatedByColumn(!showUpdatedByColumn)}
            className={`flex items-center space-x-2 px-4 py-2 rounded-lg border transition-all duration-200 ${
              showUpdatedByColumn
                ? 'bg-blue-500 border-blue-500 text-white shadow-md'
                : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
            }`}
            title={showUpdatedByColumn ? "Hide Updated By column" : "Show Updated By column"}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
            <span className="text-sm font-medium">
              {showUpdatedByColumn ? 'Hide' : 'Show'} Updated By
            </span>
          </button>
          
          <UniversalFilters
            config={filterConfig}
            values={filters}
            onChange={handleFilterChange}
            onClearFilters={clearFilters}
            mode="dropdown"
            dropdownProps={{
              buttonProps: { size: 'md' },
              contentProps: { layout: 'grid' }
            }}
          />
        </div>
      </div>

      <div className="bg-white border-2 border-blue-200 rounded-xl p-4 mb-6 shadow-sm">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="bg-blue-500 rounded-lg p-2.5">
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
            <div>
              <label className="text-sm font-semibold text-gray-900">Financial Year</label>
              <p className="text-xs text-gray-600">View and update clearance for selected year</p>
            </div>
          </div>
          
          <div className="flex items-center space-x-3">
            <select
              value={selectedFinancialYear}
              onChange={(e) => handleFinancialYearChange(e.target.value)}
              disabled={viewAllYears}
              className={`px-4 py-2 text-sm font-medium border-2 rounded-lg transition-all duration-200 ${
                viewAllYears 
                  ? 'bg-gray-100 border-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-white border-blue-300 text-gray-900 hover:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer'
              }`}
            >
              {fyOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}{option.isCurrent}
                </option>
              ))}
            </select>
            
            <button
              onClick={handleViewAllYears}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${
                viewAllYears
                  ? 'bg-blue-500 text-white border-2 border-blue-500 shadow-md'
                  : 'bg-white text-gray-700 border-2 border-gray-300 hover:border-blue-300 hover:text-blue-600'
              }`}
            >
              <div className="flex items-center space-x-2">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                </svg>
                <span>{viewAllYears ? 'Viewing All Years' : 'View All Years'}</span>
              </div>
            </button>
          </div>
        </div>
        
        {!viewAllYears && (
          <div className="mt-3 pt-3 border-t border-blue-100">
            <p className="text-xs text-gray-600">
              <span className="font-medium text-blue-700">Note:</span> Viewing clearance data for <span className="font-semibold text-blue-900">FY {selectedFinancialYear}</span>
            </p>
          </div>
        )}
      </div>

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        {departmentsToShow.length === 0 ? (
          <div className={cardPadding}>
            <EmptyState
              icon={
                <svg className="w-16 h-16 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              }
              title={hasActiveFilters ? "No projects match your filters" : "No session projects found"}
              description={
                hasActiveFilters 
                  ? "Try adjusting your search or filter criteria to see more projects"
                  : selectedFinancialYear 
                    ? `No session projects found for FY ${selectedFinancialYear}`
                    : "No session projects available for clearance"
              }
              actionLabel="Clear Filters"
              onAction={clearFilters}
            />
          </div>
        ) : (
          <div className="table-container">
            <table className="min-w-full text-xs sticky-header">
              <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
                <tr>
                  <th className="px-2 py-2 text-left text-xs font-bold text-gray-700 uppercase tracking-wider border-r border-gray-200 w-16">
                    S. No.
                  </th>
                  <th className="px-2 py-2 text-left text-xs font-bold text-gray-700 uppercase tracking-wider border-r border-gray-200 w-60">
                    Department
                  </th>
                  <th className="px-2 py-2 text-left text-xs font-bold text-gray-700 uppercase tracking-wider border-r border-gray-200 min-w-40">
                    Project name
                  </th>
                  <th className="px-2 py-2 text-center text-xs font-bold text-gray-700 uppercase tracking-wider border-r border-gray-200 w-20">
                    Amount
                    <br />
                    <span className="text-gray-500 normal-case font-normal">(in lakh)</span>
                  </th>
                  <th className="px-2 py-2 text-center text-xs font-bold text-gray-700 uppercase tracking-wider border-r border-gray-200 w-48">
                    State Plan/
                    <br />
                    Negotiated Loan
                  </th>
                  <th className="px-2 py-2 text-center text-xs font-bold text-gray-700 uppercase tracking-wider border-r border-gray-200 w-20">
                    Cleared
                    <br />
                    <span className="text-gray-500 normal-case font-normal">(in lakh)</span>
                  </th>
                  <th className="px-2 py-2 text-center text-xs font-bold text-gray-700 uppercase tracking-wider border-r border-gray-200 w-24">
                    UO No.
                  </th>
                  <th className={`px-2 py-2 text-center text-xs font-bold text-gray-700 uppercase tracking-wider w-24 ${showUpdatedByColumn ? 'border-r border-gray-200' : ''}`}>
                    Date
                  </th>
                  {showUpdatedByColumn && (
                    <th className="px-2 py-2 text-center text-xs font-bold text-gray-700 uppercase tracking-wider border-r border-gray-200 w-24">
                      Updated By
                    </th>
                  )}
                  <th className="px-2 py-2 text-center text-xs font-bold text-gray-700 uppercase tracking-wider w-20">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {(() => {
                  let continuousSerialNumber = 1;
                  
                  return departmentsToShow.map((department) => {
                    const departmentProjects = groupedProjects[department];
                    
                    return (
                      <React.Fragment key={department}>
                        {departmentProjects.map((project) => {
                          const isProjectSaving = saving[project.id];
                          const currentSerialNumber = continuousSerialNumber++;
                          const lastUpdated = formatLastUpdated(project.updatedBy || project.createdBy, project.updatedAt || project.createdAt);
                          const isProjectLocked = project.isClearanceLocked || false;
                          const canEdit = canEditProject(project);
                          
                          return (
                            <tr key={project.id} className={`table-row hover:bg-blue-50 transition-colors duration-200 ${isProjectLocked ? 'bg-orange-50' : ''}`}>
                              <td className="px-2 py-2 text-xs text-gray-600 font-semibold border-r border-gray-200">
                                <div className="flex items-center justify-center w-6 h-6 bg-gray-100 rounded-full text-xs font-bold">
                                  {currentSerialNumber}
                                </div>
                              </td>
                              
                              <td className="px-2 py-2 border-r border-gray-200">
                                <div className="text-xs text-gray-900 font-semibold px-2 py-1">
                                  {getDepartmentLabel(project.department)}
                                </div>
                              </td>
                              
                              <td className="px-2 py-2 border-r border-gray-200">
                                <div className="text-xs text-gray-900 font-semibold">
                                  {project.projectName}
                                </div>
                                {isProjectSaving && (
                                  <div className="flex items-center mt-1">
                                    <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-blue-500"></div>
                                    <span className="ml-1 text-xs text-blue-600">Saving...</span>
                                  </div>
                                )}
                              </td>
                              
                              <td className="px-2 py-2 text-center border-r border-gray-200">
                                <div className="text-xs font-bold text-gray-900 px-2 py-1">
                                  {formatSessionAmount(project.financialRequirement)}
                                </div>
                              </td>
                              
                              <td className="px-2 py-2 text-center border-r border-gray-200">
                                <div className="text-xs font-medium text-gray-900 px-2 py-1">
                                  {getClearanceTypeLabel(project.clearanceType)}
                                </div>
                              </td>
                              
                              <td className="px-2 py-2 text-center border-r border-gray-200">
                                <DebouncedInput
                                  type="number"
                                  value={project.allocatedAmount || ''}
                                  onChange={(value) => handleFieldChange(project.id, 'allocatedAmount', value)}
                                  disabled={!canEdit}
                                  delay={1200}
                                  className={`enhanced-input no-arrows w-full text-xs text-center border border-gray-300 rounded-lg px-1 py-1 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                                    errors[`${project.id}.allocatedAmount`] ? 'border-red-500 focus:ring-red-500' : ''
                                  } ${!canEdit ? 'opacity-50 cursor-not-allowed' : ''}`}
                                  placeholder="0"
                                />
                                {errors[`${project.id}.allocatedAmount`] && (
                                  <div className="text-xs text-red-600 mt-0.5 font-medium">
                                    {errors[`${project.id}.allocatedAmount`]}
                                  </div>
                                )}
                              </td>
                              
                              <td className="px-2 py-2 text-center border-r border-gray-200">
                                <DebouncedInput
                                  type="text"
                                  value={project.uoNumber || ''}
                                  onChange={(value) => handleFieldChange(project.id, 'uoNumber', value)}
                                  disabled={!canEdit}
                                  delay={1200}
                                  className={`enhanced-input w-full text-xs text-center border border-gray-300 rounded-lg px-1 py-1 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                                    errors[`${project.id}.uoNumber`] ? 'border-red-500 focus:ring-red-500' : ''
                                  } ${!canEdit ? 'opacity-50 cursor-not-allowed' : ''}`}
                                  placeholder="UO No."
                                />
                                {errors[`${project.id}.uoNumber`] && (
                                  <div className="text-xs text-red-600 mt-0.5 font-medium">
                                    {errors[`${project.id}.uoNumber`]}
                                  </div>
                                )}
                              </td>
                              
                              <td className={`px-2 py-2 text-center ${showUpdatedByColumn ? 'border-r border-gray-200' : ''}`}>
                                <input
                                  type="date"
                                  value={formatDateForInput(project.clearanceDate)}
                                  onChange={(e) => handleDateChange(project.id, e.target.value ? new Date(e.target.value) : null)}
                                  disabled={!canEdit}
                                  className={`enhanced-input w-full text-xs text-center border border-gray-300 rounded-lg px-1 py-1 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                                    errors[`${project.id}.clearanceDate`] ? 'border-red-500 focus:ring-red-500' : ''
                                  } ${!canEdit ? 'opacity-50 cursor-not-allowed' : ''}`}
                                />
                                {errors[`${project.id}.clearanceDate`] && (
                                  <div className="text-xs text-red-600 mt-0.5 font-medium">
                                    {errors[`${project.id}.clearanceDate`]}
                                  </div>
                                )}
                              </td>
                              
                              {showUpdatedByColumn && (
                                <td className="px-2 py-2 text-center border-r border-gray-200">
                                  <div className="text-xs text-gray-600">
                                    <div className="font-medium text-gray-800 mb-0.5">
                                      {lastUpdated.user}
                                    </div>
                                    <div className="text-gray-500">
                                      {lastUpdated.time}
                                    </div>
                                  </div>
                                </td>
                              )}
                              
                              <td className="px-2 py-2 text-center">
                                <div className="flex items-center justify-center space-x-1">
                                  {isProjectLocked ? (
                                    isAdmin ? (
                                      <button
                                        onClick={() => handleUnlockProject(project.id)}
                                        className="p-1.5 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                                        title={`Unlock clearance (Locked by ${project.clearanceLockedByName})`}
                                      >
                                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z" />
                                        </svg>
                                      </button>
                                    ) : (
                                      <div className="p-1.5 text-orange-400" title={`Locked by ${project.clearanceLockedByName}`}>
                                        <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                                          <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                                        </svg>
                                      </div>
                                    )
                                  ) : (
                                    <button
                                      onClick={() => handleLockProject(project.id)}
                                      disabled={saving[project.id]}
                                      className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors disabled:opacity-50"
                                      title="Lock clearance"
                                    >
                                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                      </svg>
                                    </button>
                                  )}
                                  
                                  {canEdit ? (
                                    <button
                                      onClick={() => handleDelete(project.id)}
                                      disabled={saving[project.id]}
                                      className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                                      title="Delete project"
                                    >
                                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                      </svg>
                                    </button>
                                  ) : null}
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </React.Fragment>
                    );
                  });
                })()}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default ClearanceProjectWise;